import React, { useEffect, useState } from 'react';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useCountries, useStates } from '../../../hooks/useGeoUnits';

// state is a Geography UUID; the country selector only narrows the state list.

export const emptyLocationRow = () => ({ state: '', full_address: '', purpose: '' });

export function locationsToPayload(rows) {
    return rows
        .filter(row => row.state && row.full_address?.trim())
        .map(row => ({
            state: row.state,
            full_address: row.full_address.trim(),
            purpose: row.purpose?.trim() || '',
        }));
}

export function locationsFromApi(locations) {
    return (locations || []).map(l => ({
        state: l.state || '',
        full_address: l.full_address || '',
        purpose: l.purpose || '',
    }));
}

export default function LocationEditor({ value = [], onChange, disabled = false, error }) {
    const { data: countries = [] } = useCountries();
    const [countryId, setCountryId] = useState('');

    // Defaults to the first country so the state dropdown works immediately.
    useEffect(() => {
        if (!countryId && countries.length > 0) setCountryId(countries[0].id);
    }, [countries, countryId]);

    const { data: states = [], isLoading: loadingStates } = useStates(countryId);

    const update = (index, patch) => {
        onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };

    const remove = (index) => onChange(value.filter((_, i) => i !== index));

    return (
        <div>
            <div className="flex items-center justify-between mb-10">
                <label className="text-sm font-medium">
                    Locations <span className="text-danger">*</span>
                </label>
                {!disabled && (
                    <button
                        type="button"
                        onClick={() => onChange([...value, emptyLocationRow()])}
                        className="btn btn-outline-secondary text-xs px-10 py-5 flex items-center gap-5"
                    >
                        <IconPlus size={14} /> Add location
                    </button>
                )}
            </div>

            {!disabled && countries.length > 1 && (
                <select
                    value={countryId}
                    onChange={(e) => setCountryId(e.target.value)}
                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none mb-10"
                >
                    {countries.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            )}

            {value.length === 0 ? (
                <p className="text-xs text-text-secondary border border-dashed border-border rounded-[4px] px-10 py-15 text-center">
                    Add at least one destination.
                </p>
            ) : (
                <div className="flex flex-col gap-10">
                    {value.map((row, index) => (
                        <div key={index} className="border border-border rounded-[4px] p-10">
                            <div className="flex items-center justify-between mb-8">
                                <span className="text-xs font-medium text-text-secondary">Stop {index + 1}</span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="text-text-secondary hover:text-danger"
                                    >
                                        <IconTrash size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="grid md:grid-cols-3 grid-cols-1 gap-8">
                                <select
                                    value={row.state}
                                    disabled={disabled || loadingStates}
                                    onChange={(e) => update(index, { state: e.target.value })}
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                >
                                    <option value="">{loadingStates ? 'Loading states...' : 'Select state'}</option>
                                    {states.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    value={row.full_address}
                                    disabled={disabled}
                                    onChange={(e) => update(index, { full_address: e.target.value })}
                                    placeholder="Full address"
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                />
                                <input
                                    type="text"
                                    value={row.purpose}
                                    disabled={disabled}
                                    onChange={(e) => update(index, { purpose: e.target.value })}
                                    placeholder="Purpose of this stop"
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none disabled:opacity-60"
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {error && <p className="text-danger text-xs mt-5">{error}</p>}
        </div>
    );
}
